import type { ClubRole } from "./auth";
import { SLUG_MIN, slugProblem, slugSuggestion } from "./tournament";
import { clampJerseyNumber, clampRosterRow, type Player, type Team } from "./types";
import type { TournamentSummary } from "./tournament";

/** Historical squads retain their team ids; explicit club ownership wins over
 * the legacy convention where the club and team shared an id. */
export function clubTeams(teams: readonly Team[], clubId: string): Team[] {
  return teams.filter((team) => (team.clubId ?? team.id) === clubId);
}

/** Resolve both explicit ownership and legacy shared ids against real clubs.
 * A standalone team must not acquire a club link just because it has an id. */
export function clubOfTeam(
  team: Pick<Team, "id" | "clubId">,
  clubs: readonly ClubSummary[],
): ClubSummary | undefined {
  return clubs.find((club) => club.id === (team.clubId ?? team.id));
}

export type ClubTournament = TournamentSummary & {
  /** `division`: the squad's division name at that event, when it has one. */
  teams: { id: string; name: string; division?: string }[];
};

/**
 * A club's squad as a list of that event's squads names it (2026-09-15): the
 * name alone, or with its division when the club has more than one squad
 * there («Nova · Open», «Nova · Women's»). Two squads often share a name.
 */
export function squadLabel(team: { name: string; division?: string }, squadsAtEvent: number): string {
  return squadsAtEvent > 1 && team.division ? `${team.name} · ${team.division}` : team.name;
}

/**
 * A club outlives an event and owns a library roster with stable player ids.
 * Each event squad is an independent Team with `clubId` pointing here. The
 * import copies selected library players; later changes remain event-local.
 * Only explicit additive sync changes the library. Legacy API callers without
 * a request id retain their original first-team = club-id behavior.
 * See the 2026-09-11 owner correction in V3-RESEARCH.md (supersedes D10/D28).
 */

export interface Club {
  /** A uuid for a club created here; a promoted team keeps its team id. */
  id: string;
  /** The page path segment: `/team/<slug>`. */
  slug: string;
  name: string;
  short: string;
  color: string;
  logo?: string;
  logoDark?: string;
  city?: string;
  roster: Player[];
  createdAt: number;
  /** `principalRef` of who created or promoted it. */
  createdBy: string;
  updatedAt: number;
}

/** The public list row carries image addresses, never inline crests or a
 * roster: a large directory must not download every image before it paints. */
export interface ClubSummary {
  id: string;
  slug: string;
  name: string;
  short: string;
  color: string;
  city?: string;
  players: number;
  updatedAt: number;
  logoUrl?: string;
  logoDarkUrl?: string;
}

export function clubSummary(club: Club): ClubSummary {
  return {
    id: club.id,
    slug: club.slug,
    name: club.name,
    short: club.short,
    color: club.color,
    ...(club.city !== undefined ? { city: club.city } : {}),
    players: club.roster.length,
    updatedAt: club.updatedAt,
    ...(club.logo ? { logoUrl: clubLogoUrl(club) } : {}),
    ...(club.logoDark ? { logoDarkUrl: clubLogoUrl(club, true) } : {}),
  };
}

/** A revision in the address lets a new upload bypass an older browser cache. */
function clubLogoUrl(club: Pick<Club, "id" | "updatedAt">, dark = false): string {
  return `/api/clubs/${encodeURIComponent(club.id)}/logo?v=${club.updatedAt}${dark ? "&variant=dark" : ""}`;
}

/** What creating or editing a club carries; the id and the slug are the
 *  registry's, the timestamps the server's. */
export interface ClubInput {
  name: string;
  short: string;
  color: string;
  logo?: string;
  logoDark?: string;
  city?: string;
  roster: Player[];
}

export type ClubPatch = Partial<ClubInput>;

/** One account with a seat on a club, as the club page lists them. Since V3
 *  Phase 1 an account may carry a Telegram identity instead of an email. */
export interface ClubMember {
  id: string;
  label: string;
  email?: string;
  telegram?: { id: number; username?: string };
  role: ClubRole;
  playerId?: string;
  grantedAt: number;
  lastLoginAt?: number;
  disabledAt?: number;
}

/**
 * Segments that live under `/team/` and `/t/<slug>/team/<club>/` as ROUTES,
 * which a club slug must therefore never be. The router ranks a static
 * segment above a dynamic one, so a club called "games" would simply never
 * open; refusing the slug says so up front.
 */
export const CLUB_RESERVED_SLUGS = [
  "games",
  "standings",
  "spirit",
  "roster",
  "rules",
  "regulations",
  "new",
  "import",
] as const;

/**
 * A slug for a club, from its name: the tournament's transliteration, padded
 * to the minimum length, kept off the reserved list and off whatever is
 * `taken`, by suffixing `-2`, `-3`, … A club is named once and rarely typed
 * into anything, so a suffix is fine where a tournament slug would be retyped
 * by its organiser.
 */
export function clubSlugFrom(name: string, taken: (slug: string) => boolean): string {
  let base = slugSuggestion(name);
  if (base.length < SLUG_MIN) base = base === "" ? "club" : `${base}-club`;
  // A malformed base is replaced; a RESERVED one is only suffixed, like any
  // other collision, so a club called "Spirit" becomes `spirit-2`, not `club`.
  if (slugProblem(base) === "shape") base = "club";
  const blocked = (s: string): boolean =>
    (CLUB_RESERVED_SLUGS as readonly string[]).includes(s) ||
    slugProblem(s) === "reserved" ||
    taken(s);
  if (!blocked(base)) return base;
  for (let n = 2; n < 1000; n += 1) {
    const candidate = `${base}-${n}`;
    if (!blocked(candidate)) return candidate;
  }
  return `${base}-${Date.now().toString(36)}`;
}

// ---------- import ----------

/** One library player's fate in this tournament: in or out, and the number
 *  they wear here when it differs from the library's. */
export interface ImportChoice {
  playerId: string;
  include: boolean;
  number?: string;
}

/** The identity fields the organiser may override per tournament, and where
 *  the team plays. Everything absent comes from the club. */
export interface ImportOverrides {
  name?: string;
  short?: string;
  color?: string;
  divisionId?: string;
  poolId?: string;
}

/**
 * The tournament team an import writes: the caller's independent team id, the
 * club's identity unless overridden, the library players the organiser kept
 * (a player with no choice listed is IN - R4's default is on) carrying their
 * library ids, then the new players as given. Nothing here touches the club.
 */
export function importPlan(
  club: Club,
  choices: readonly ImportChoice[],
  added: readonly Player[],
  overrides: ImportOverrides,
  now: number,
  teamId = club.id,
): Team {
  const byId = new Map(choices.map((c) => [c.playerId, c]));
  const kept: Player[] = [];
  for (const player of club.roster) {
    const choice = byId.get(player.id);
    if (choice !== undefined && !choice.include) continue;
    const number = choice?.number;
    // A library row is copied as the library has it, and a library may hold a
    // row from before the roster's limits reached every writer (2026-09-14):
    // kept inside them, or the event's first roster save is refused whole.
    kept.push(clampRosterRow(number !== undefined ? { ...player, number } : { ...player }));
  }
  // A new player whose id collides with a library one would silently merge
  // two people; the plan keeps the library's and drops the impostor.
  const libraryIds = new Set(club.roster.map((p) => p.id));
  const fresh = added.filter((p) => !libraryIds.has(p.id));
  return {
    id: teamId,
    name: overrides.name ?? club.name,
    short: overrides.short ?? club.short,
    color: overrides.color ?? club.color,
    ...(club.logo !== undefined ? { logo: club.logo } : {}),
    ...(club.logoDark !== undefined ? { logoDark: club.logoDark } : {}),
    roster: [...kept, ...fresh],
    clubId: club.id,
    library: { importedAt: now },
    ...(overrides.divisionId !== undefined ? { divisionId: overrides.divisionId } : {}),
    ...(overrides.poolId !== undefined ? { poolId: overrides.poolId } : {}),
  };
}

/**
 * The library players already on a team at THIS event, each with the names of
 * the teams whose rosters carry them. R4's "everyone in" was decided when a
 * club entered an event once; since a club may enter several squads
 * (2026-09-11) it hands the second squad the first one's players. On
 * 2026-09-13 Вовчиці was registered a minute after VinWolves and went in with
 * VinWolves' seven men still switched on. Any team counts, the club's own or
 * not: a library player on somebody else's sheet here is not coming with this
 * squad either.
 */
export function enteredElsewhere(
  club: Pick<Club, "roster">,
  teams: readonly Pick<Team, "name" | "roster">[],
): Map<string, string[]> {
  const library = new Set(club.roster.map((p) => p.id));
  const entered = new Map<string, string[]>();
  for (const team of teams) {
    for (const player of team.roster ?? []) {
      if (!library.has(player.id)) continue;
      const names = entered.get(player.id) ?? [];
      // One sheet listing a player twice still names its team once.
      if (!names.includes(team.name)) names.push(team.name);
      entered.set(player.id, names);
    }
  }
  return entered;
}

/**
 * Where a library picker starts: every player in (R4), except the ones
 * `enteredElsewhere` found, who start out. A starting point and never a
 * refusal: switching one back on is a deliberate act the form allows, for the
 * event that does let somebody play two divisions.
 */
export function startingChoices(
  club: Pick<Club, "roster">,
  entered: ReadonlyMap<string, readonly string[]>,
): Record<string, ImportChoice> {
  return Object.fromEntries(
    club.roster.map((p) => [p.id, { playerId: p.id, include: !entered.has(p.id) }]),
  );
}

// ---------- sync back ----------

export interface SyncPlan {
  /** The library roster after the merge. */
  roster: Player[];
  /** Tournament players the library did not have. */
  added: Player[];
  /** Library players whose number this tournament changed. */
  renumbered: { id: string; name: string; from: string; to: string }[];
  /** Library players untouched by this tournament's copy. */
  unchanged: number;
}

/**
 * Merge a tournament's roster INTO the library, additively (D11): a player the
 * library lacks is added with the id they already carry; a shared id takes the
 * tournament's number (the sheet the captain corrected this weekend is the
 * fresher fact); nothing is removed and no name is rewritten - a rename is a
 * decision for the club page, not a side effect of a sync.
 */
export function syncPlan(team: Pick<Team, "roster">, club: Pick<Club, "roster">): SyncPlan {
  const tournament = new Map((team.roster ?? []).map((p) => [p.id, p]));
  const roster: Player[] = [];
  const renumbered: SyncPlan["renumbered"] = [];
  let unchanged = 0;
  for (const player of club.roster) {
    const own = tournament.get(player.id);
    // The event's number as a roster may keep it: a sync is not a way past
    // the limits (a pickup row from before 2026-09-14 could carry 8 chars).
    const number = own === undefined ? undefined : clampJerseyNumber(own.number);
    if (number !== undefined && number !== player.number) {
      renumbered.push({ id: player.id, name: player.name, from: player.number, to: number });
      roster.push({ ...player, number });
    } else {
      if (number === undefined || number === player.number) unchanged += 1;
      roster.push({ ...player });
    }
  }
  const libraryIds = new Set(club.roster.map((p) => p.id));
  // A pickup played for this squad, not for the club: the library never gains one.
  const added = (team.roster ?? [])
    .filter((p) => !libraryIds.has(p.id) && p.pickup !== true)
    .map(({ pickup: _event, ...p }) => ({ ...clampRosterRow(p) }));
  return { roster: [...roster, ...added], added, renumbered, unchanged };
}

// ---------- promote ----------

/**
 * An ad-hoc team becomes a club, keeping its id and its players' ids, so a
 * later import of the same club lands on the same team id and the same
 * people (D10, D11). The slug is the caller's (it needs the registry to know
 * what is taken); the identity is the team's.
 */
export function promotePlan(team: Team, slug: string, createdBy: string, now: number): Club {
  return {
    id: team.id,
    slug,
    name: team.name,
    short: team.short,
    color: team.color,
    ...(team.logo !== undefined ? { logo: team.logo } : {}),
    ...(team.logoDark !== undefined ? { logoDark: team.logoDark } : {}),
    // Inside a roster's limits: the new library is saved whole from here on.
    roster: (team.roster ?? []).map((p) => ({ ...clampRosterRow(p) })),
    createdAt: now,
    createdBy,
    updatedAt: now,
  };
}

/** Private request, separate from the public roster and from an event entry. */
export interface ClubApplication {
  accountId: string;
  clubId: string;
  kind: "player" | "support";
  number: string;
  message: string;
  /** Missing only on applications submitted before names were collected. */
  firstName?: string;
  lastName?: string;
  at: number;
  /** Preallocated public row id makes a retry after a cross-object write safe. */
  newPlayerId: string;
}
export type ApplicationInput = Pick<ClubApplication, "kind" | "number" | "message"> & {
  firstName: string;
  lastName: string;
};

export type ApplicationDecision = {
  requestId: string;
  decision: "accept" | "decline";
  playerId?: string;
  /** Name for a new club row; never changes the applicant's account identity. */
  newPlayer?: { firstName: string; lastName: string };
  note?: string;
};

/** Put a seat on the roster: an unclaimed row, or a new row named for the person. */
export type MemberPlayerLink =
  | { playerId: string }
  | { newPlayer: { firstName: string; lastName: string } };

/** Keep the resulting name within the existing roster's 40-character limit. */
export function applicationPlayerName(firstName: string, lastName: string): string | null {
  const first = firstName.trim().replace(/\s+/g, " ");
  const last = lastName.trim().replace(/\s+/g, " ");
  const name = `${first} ${last}`;
  return first && last && name.length <= 40 ? name : null;
}

/** Applicants enter English transliterations; reviewers can still fix old names. */
export function applicationEnglishName(firstName: string, lastName: string): string | null {
  const name = applicationPlayerName(firstName, lastName);
  return name && /^[A-Za-z]+(?:[ '’-][A-Za-z]+)*$/.test(name) ? name : null;
}

/** Suggest, never claim: a reviewer still explicitly approves this choice. */
export function applicationRosterLink(
  application: Pick<ClubApplication, "firstName" | "lastName">,
  roster: readonly Player[],
  members: readonly ClubMember[],
): MemberPlayerLink | null {
  const { firstName, lastName } = application;
  if (!firstName || !lastName) return null;
  const name = applicationPlayerName(firstName, lastName);
  if (!name) return null;
  const normalize = (value: string) => value.trim().replace(/\s+/g, " ").toLowerCase();
  const matches = roster.filter((p) => normalize(p.name) === normalize(name));
  if (matches.length === 0) return { newPlayer: { firstName, lastName } };
  const match = matches[0];
  // Ambiguous names or an already claimed identity need a manual choice.
  return matches.length === 1 && match && !members.some((m) => m.playerId === match.id)
    ? { playerId: match.id }
    : null;
}

export function playerClaimed(members: readonly ClubMember[], playerId: string, exceptAccountId: string): boolean {
  return members.some((m) => m.id !== exceptAccountId && m.playerId === playerId);
}

/** Adding from the club never overwrites existing event numbers or removes
 * guests. The event's saved/drafted row wins on an id collision. */
export function addClubPlayers(current: readonly Player[], selected: readonly Player[]): Player[] {
  const rows = new Map(current.map((p) => [p.id, p]));
  for (const player of selected) if (!rows.has(player.id)) rows.set(player.id, player);
  return [...rows.values()];
}
